import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme types
export const THEMES = {
  LANDING: 'landing',
  GM: 'gm',
  PLAYER: 'player'
};

// Create context
const ThemeContext = createContext({
  theme: THEMES.LANDING,
  setTheme: () => {},
  isGMMode: false,
  isPlayerMode: false
});

// Theme provider component
//
// The theme is derived from the route: App.js sets it on every navigation, so
// the route is the single source of truth. This provider used to also write
// the theme to localStorage and read it back on mount, which could not work
// and was not wanted:
//
//   - the persist effect is declared first, so on mount it wrote the default
//     over the stored value before the restore effect could read it;
//   - even if it had restored, App.js overwrites the theme from the route
//     immediately afterwards;
//   - restoring would be wrong anyway -- a stored 'gm' theme would flash GM
//     styling on the landing page before the route corrected it.
//
// Removing it also removes a crash: localStorage throws rather than returning
// null in Safari private browsing and with site data blocked, and an uncaught
// throw in a provider effect unmounts the whole app.
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(THEMES.LANDING);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove all theme attributes
    root.removeAttribute('data-theme');
    
    // Apply new theme if not landing (landing uses root defaults)
    if (theme !== THEMES.LANDING) {
      root.setAttribute('data-theme', theme);
    }
    
  }, [theme]);

  const value = {
    theme,
    setTheme,
    isGMMode: theme === THEMES.GM,
    isPlayerMode: theme === THEMES.PLAYER,
    isLandingMode: theme === THEMES.LANDING
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook for consuming theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper component for conditional rendering based on theme
export const ThemedComponent = ({ gmContent, playerContent, children }) => {
  const { isGMMode, isPlayerMode } = useTheme();
  
  if (isGMMode && gmContent) return gmContent;
  if (isPlayerMode && playerContent) return playerContent;
  return children;
};

export default ThemeContext;
